// @ts-strict-ignore
import { getLocale } from './i18n';
import {
  DEFAULT_CURRENCY_CODE,
  DEFAULT_MINOR_UNIT,
  CURRENCIES_BY_ALPHABETIC_CODE,
  COUNTRIES_BY_ALPHA_TWO_CODE,
} from './constants';

/**
 * @description this is a helper function for looking up currency data. This is mostly
 *    a helper function for this file, but is useful to look up things about a currency
 *    (like it's minor unit) when necessary for 3rd party tools (like currency input
 *    formatting).
 *
 * @param specifications an object, used to determine which currency should be
 *    retrieved. Ideally, this is a 3-char long alphabetic code, but we also support
 *    looking up by a countries Alpha 2 code as a fall back (provided that country
 *    is supported)
 *
 * @returns the currencyData fetched from our `/i18n/constants.ts` file. Defaults to USD
 *    is nothing can be found
 */
interface GetCurrencyData {
  alphabeticCurrencyCode?: string | null;
  alphaTwoCountryCode?: string | null;
}
export const getCurrencyData = (
  specifications: GetCurrencyData = {
    alphabeticCurrencyCode: null,
    alphaTwoCountryCode: null,
  },
) => {
  const alphabeticCurrencyCode = specifications.alphabeticCurrencyCode
    ? specifications.alphabeticCurrencyCode.toUpperCase()
    : null;
  const alphaTwoCountryCode = specifications.alphaTwoCountryCode
    ? specifications.alphaTwoCountryCode.toUpperCase()
    : null;

  const currencyData = CURRENCIES_BY_ALPHABETIC_CODE[alphabeticCurrencyCode];
  const countryData = COUNTRIES_BY_ALPHA_TWO_CODE[alphaTwoCountryCode];

  let returnData = CURRENCIES_BY_ALPHABETIC_CODE[DEFAULT_CURRENCY_CODE];

  if (alphabeticCurrencyCode && currencyData) {
    returnData = currencyData;
  } else if (alphaTwoCountryCode && countryData) {
    const primaryCurrencyData = CURRENCIES_BY_ALPHABETIC_CODE[countryData.primaryCurrency.code];
    if (primaryCurrencyData) {
      returnData = primaryCurrencyData;
    }
  }
  return returnData;
};

/**
 * @description Not all countries/currencies use the same degree of specificity for
 *    their decimal. This helper function is meant to determine how to convert an integer
 *    into the correct decimal value, according to country/currency specificity.
 *
 * @param rawInteger a number input to format into a decimal, based on currency/country
 *    data. Errors will be thrown if decimal values are provided
 * @param specifications an object, used to determine how an integer should be formatted
 *    into a decimal. This is essentially a lookup helper!
 *
 * @returns a formatted decimal value
 */
interface GetNumberWithDecimal {
  alphabeticCurrencyCode?: string | null;
  alphaTwoCountryCode?: string | null;
}
export const getNumberWithDecimal = (
  rawInteger: number,
  specifications: GetNumberWithDecimal = {
    alphabeticCurrencyCode: null,
    alphaTwoCountryCode: null,
  },
) => {
  const originalNumberIsDecimal = rawInteger.toString().includes('.');
  if (originalNumberIsDecimal) {
    // This is weird, but important for a few reasons:
    // - JS/TS has no concept of integers vs decimals. there are just numbers
    // - Formatting decimals is inconsistent and can result in data being
    //   trimmed away
    // - All amounts SHOULD be stored in our DB as integers anyways, to account
    //   for internationalization anyways
    throw new Error('Cannot provide decimal to function expecting integers');
  }

  // Default to US standards if country cannot be determined
  const alphabeticCurrencyCode = specifications?.alphabeticCurrencyCode
    ? specifications.alphabeticCurrencyCode.toUpperCase()
    : null;
  const alphaTwoCountryCode = specifications?.alphaTwoCountryCode
    ? specifications.alphaTwoCountryCode.toUpperCase()
    : null;
  let minorUnit: number = DEFAULT_MINOR_UNIT;
  const currencyData = getCurrencyData({ alphabeticCurrencyCode, alphaTwoCountryCode });
  minorUnit = currencyData.minorUnit;

  // NOTE from Patriq: "I think this rule/equation is correct for all currency systems
  //      Essentially this computation says that if the specified currency has an
  //      integer minor unit, `numberToFormat = (numberToFormat/(Math.pow(10, minorUnit)))`.
  //      This certainly works for minor units of 0 and 2, and appears to work for 3 & 4
  //      but they are rarer and I am less familiar"
  let numberToFormat = rawInteger;
  numberToFormat /= (10 ** minorUnit);
  return Number(numberToFormat.toFixed(minorUnit));
};

// the "CurrencySpecifications" interface is for providing information about the
// currency/country itself, which is then used to determine how we modify the integer
// we received as input. As of writing this, we only support alphabeticCurrencyCode
// and localeOverride, but in the future we may also want to support passing in the
// currency's numeric code, the country's alpha-3 code, or the country's alpha-2 code.
interface CurrencySpecifications {
  alphabeticCurrencyCode: string | null;
  localeOverride?: string | null;
}
// the "FormattingOptions" interface is for specifying formatting options to pass
// into JS' internationalization API
// Note: signDisplay is for displaying "-" for negative numbers
interface CurrencyFormattingOptions {
  roundingMode?: 'ceil' | 'floor' | 'expand' | 'trunc' | 'halfCeil' | 'halfFloor' | 'halfExpand' | 'halfTrunc' | 'halfEven';
  signDisplay?: 'auto' | 'never' | 'always';
}

/**
 * @description this function is a essentially a wrapper for JS's internationalization
 *    API, as a way to simplify currency formatting (since there are a lot of params
 *    and that tool can be used for all sorts of number formatting)
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
 *
 * @param {number} originalNumber - should be an integer, for the number that
 *    will be formatted
 * @param {CurrencySpecifications} specifications - is an object with intl data
 *    about the thing we are trying to format (like currency or locale override).
 *    These things are a important because it dictates how we actually modify
 *    data (like where to place a decimal value).
 * @param {CurrencyFormattingOptions} formatOptions - is an object with params
 *    specific to Javascript's internationalization API (for formatting only)
 *
 * @returns object containing multiple fields relevant to what/how we formatted the input
 */
export const formatCurrencyIntl = (
  originalNumber: number,
  specifications: CurrencySpecifications = { alphabeticCurrencyCode: null, localeOverride: null },
  formatOptions: CurrencyFormattingOptions = { roundingMode: 'halfExpand', signDisplay: 'auto' },
) => {
  // (1) pre-process & setup options for JS's internationalization API
  const originalNumberIsDecimal = originalNumber.toString().includes('.');
  if (originalNumberIsDecimal) {
    // This is weird, but important for a few reasons:
    // - JS/TS has no concept of integers vs decimals. there are just numbers
    // - Formatting decimals is inconsistent and can result in data being
    //   trimmed away
    // - All amounts SHOULD be stored in our DB as integers anyways, to account
    //   for internationalization anyways
    throw new Error('Cannot provide decimal to function expecting integers');
  }
  const alphabeticCurrencyCode = specifications?.alphabeticCurrencyCode
    ? specifications.alphabeticCurrencyCode.toUpperCase()
    : null;
  const currency = CURRENCIES_BY_ALPHABETIC_CODE[alphabeticCurrencyCode]
    ? alphabeticCurrencyCode
    : DEFAULT_CURRENCY_CODE;
  const numberWithDecimal = getNumberWithDecimal(originalNumber, {
    alphabeticCurrencyCode: currency,
  });
  const style = 'currency';
  const locale = specifications.localeOverride || getLocale();

  // (2) Do the actual formatting
  const formattedNumber = new Intl.NumberFormat(locale, {
    ...formatOptions,
    style,
    currency,
  }).format(numberWithDecimal);

  // (3) Return object of number info
  return {
    originalNumber,
    alphabeticCurrencyCode: currency,
    numberWithDecimal,
    formattedNumber,
  };
};

// TODO: add a util for formatting normal numbers using new Intl.NumberFormat()
// TODO: add a util for formatting percentage number new Intl.NumberFormat()
